import m from 'mithril'
import _, { cloneDeep, remove as removeMutate } from 'lodash'

import OptimusConfigurationModel from '@app/models/optimus-configuration'
import type { SignalData } from '@app/models/optimus-configuration'
import type { IPhaseMappingData, IPhaseMapping, IAtomicMovement } from '@app/queries/optimus/optimus-config'

import CloseIcon from '@app/icons/x-close'
import PlusCircleIcon from '@app/icons/plus-circle'
import SingleSelect from '#frontend/components/single-select/index.js'
import InputCheckbox from '@client-shared/components-v2/input-checkbox'
import IconButton from '@client-shared/components-v2/icon-button'
import { ValidationErrorMessaging } from '../configuration-body/validation-error-messaging'

import { getEdgeConnectionNames, sortByDirectionAndTurn } from '@shared/utils/entities'
import { PRIORITY_ENUM, TURN_ENUM } from '@shared/constants'
import { initMithrilComponent } from '@client-shared/utils/mithril'
import type { IHeaderSelections, IHeaderOptions, SignalTabKey } from '../optimus-configuration'

import { OverlapViewSwitcher } from './overlap-view-switcher'
import css from './phase-mapping.module.css'
import UnstyledButton from '@client-shared/components-v2/unstyled-button'

// ─── Types ───────────────────────────────────────────────────────────────────

export const MOVEMENT_TYPE_ENUM = Object.freeze({
	overlap: 'overlap',
	pedestrian: 'pedestrian',
})

export const ADMIN_UNNAMED_VIEW = '__admin_unnamed__'
export type OverlapView = string | null

export interface IPhaseMappingSignalData extends SignalData {
	edge_connections?: IEdgeConnection[]
}

export interface IEdgeConnection {
	id: string
	edge_ids: string[]
	intersection_id: string
	start_edge_id: string
	end_edge_id: string
	turn: string
	turn_angle: number
	start_edge: IEdge
	end_edge: IEdge
	name: string
}

interface IEdge {
	id: string
	road_name: string
	road_type: string
	number_of_lanes: number
	speed_limit_meters_per_second: number
	intersection_id: string | null
	cardinal_direction?: string
	gps: {
		type: string
		crs: { type: string; properties: { name: string } }
		coordinates: [number, number][]
	}
	state: string
}

export interface IPhaseMappingMapData {
	signal_specs: IPhaseMappingSignalData | undefined
	phase_mapping: IPhaseMappingData | undefined
	active_phases: { id: number; display: number }[]
	selected_overlap_view: OverlapView
	onPhaseChange: (edge_connection_id: string, new_phase_number: number) => void
}

export interface IPhaseMappingTabAttrs {
	headerSelections: IHeaderSelections
	headerOptions: IHeaderOptions
	loading: boolean
	staticData?: IPhaseMappingData[]
	should_reload_page: number
	warnings: Record<string, Record<string, never>>
	handleModificationState: (pageKey: SignalTabKey, isModified: boolean) => void
	onMapDataChange: (data: IPhaseMappingMapData) => void
}

interface IPhaseMappingTabState {
	page_key: string
	specs_key: string
	network_id: string
	signal_id: string
	phase_plan_id: string
	phase_plan_external_id: string
	signal_phase_mappings: IPhaseMappingData[]
	phase_mapping?: IPhaseMappingData
	signal_specs: IPhaseMappingSignalData
	active_phases: { id: number; display: number }[]
	should_reload_page: number

	selected_overlap_view: OverlapView
	dropdown_open: boolean
	new_overlap_name_input: string | null
	pending_overlap_name: string | null
	renaming_overlap_name: string | null
	renaming_overlap_value: string

	syncFromAttrs: (vnode: m.Vnode<IPhaseMappingTabAttrs, IPhaseMappingTabState>) => void
	commitMappingUpdate: () => void
	notifyModification: () => void
	emitMapData: () => void
	renameOverlap: (old_name: string, new_name: string) => void
	deleteOverlap: (name: string) => void
	assignNameToRow: (am: IAtomicMovement, raw_name: string) => void
	commitNewOverlap: () => void
	commitRename: () => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizePhaseMappings(mappings: IPhaseMappingData[] = []): IPhaseMappingData[] {
	return mappings
		.map((plan) => ({
			...plan,
			phase_mapping: (plan.phase_mapping ?? [])
				.map((phase) => ({
					...phase,
					atomic_movements: (phase.atomic_movements ?? [])
						.slice()
						.sort((a, b) => (a.key ?? '').localeCompare(b.key ?? '')),
				}))
				.sort((a, b) => a.number - b.number),
		}))
		.sort((a, b) => (a.phase_settings_id ?? '').localeCompare(b.phase_settings_id ?? ''))
}

function getMovementTurnType(
	edge_connection_id: string | undefined,
	signal_specs: IPhaseMappingSignalData | undefined,
): string | undefined {
	if (!edge_connection_id || !signal_specs?.edge_connections) return undefined
	const ec = signal_specs.edge_connections.find((e) => e.id === edge_connection_id)
	return ec?.turn
}

function filterForView(am: IAtomicMovement, view: OverlapView): boolean {
	if (view === null) return !am.overlap_name
	if (view === ADMIN_UNNAMED_VIEW) return am.movement_type === MOVEMENT_TYPE_ENUM.overlap && !am.overlap_name
	return am.overlap_name === view
}

function collectOverlapNames(phase_mapping?: IPhaseMappingData): string[] {
	const seen: string[] = []
	for (const p of phase_mapping?.phase_mapping ?? []) {
		for (const am of p.atomic_movements ?? []) {
			if (am.overlap_name && !seen.includes(am.overlap_name)) seen.push(am.overlap_name)
		}
	}
	return seen
}

function findStaticMovement(
	static_data: IPhaseMappingData[] | undefined,
	phase_plan_id: string | undefined,
	movement_key: string | undefined,
): IAtomicMovement | undefined {
	const plan = static_data?.find((p) => p.phase_settings_id === phase_plan_id)
	if (!plan) return undefined
	for (const phase of plan.phase_mapping ?? []) {
		const found = phase.atomic_movements?.find((am) => am.key === movement_key)
		if (found) return found
	}
	return undefined
}

function normalizePermissive(mappings: IPhaseMappingData[]): IPhaseMappingData[] {
	for (const plan of mappings) {
		for (const phase of plan.phase_mapping ?? []) {
			for (const am of phase.atomic_movements ?? []) {
				if (am.is_permissive == null) am.is_permissive = false
			}
		}
	}
	return mappings
}

function sortPhases(phases: IPhaseMapping[]): void {
	phases.sort((a, b) => {
		if (a.name && b.name) return a.name < b.name ? -1 : 1
		return a.number < b.number ? -1 : 1
	})
}

function moveEdgeConnectionToPhase(
	phase_mapping: IPhaseMappingData,
	edge_connection_id: string,
	new_phase_number: number,
	view: OverlapView = null,
	signal_specs?: IPhaseMappingSignalData,
): void {
	const phases = phase_mapping.phase_mapping ?? []

	const moved: IAtomicMovement[] = []
	for (const phase of phases) {
		const remaining: IAtomicMovement[] = []
		for (const am of phase.atomic_movements ?? []) {
			if (am.edge_connection_id === edge_connection_id && filterForView(am, view)) moved.push(am)
			else remaining.push(am)
		}
		phase.atomic_movements = remaining
	}

	removeMutate(phases, (p) => p.atomic_movements.length === 0)

	const to_add: IAtomicMovement[] = view === null ? (moved.length ? [moved[0]] : []) : moved

	if (!to_add.length) {
		to_add.push(
			buildNewMovement(phase_mapping.phase_settings_id, new_phase_number, edge_connection_id, view, signal_specs),
		)
	}

	const target = phases.find((p) => p.number === new_phase_number)
	if (target) {
		target.atomic_movements.push(...to_add)
	} else {
		phases.push({ number: new_phase_number, atomic_movements: to_add })
	}

	sortPhases(phases)
}

function markEmptyMappingForDeletion(phase_mapping: IPhaseMappingData): void {
	if (phase_mapping.phase_mapping.length > 0) return
	const { atlas_phase_mapping_id, valid_from, valid_to } = phase_mapping
	if (atlas_phase_mapping_id && !atlas_phase_mapping_id.includes('new-')) {
		OptimusConfigurationModel.addToDeleteList(
			'phase_mapping',
			atlas_phase_mapping_id,
			'',
			valid_from ?? null,
			valid_to ?? null,
			true,
		)
	}
}

function buildNewMovement(
	phase_plan_id: string,
	phase_number: number,
	edge_connection_id: string,
	overlap_name: OverlapView = null,
	signal_specs?: IPhaseMappingSignalData,
): IAtomicMovement {
	const stamp = Date.now()
	const resolved_name = overlap_name === ADMIN_UNNAMED_VIEW ? null : overlap_name
	const turn = getMovementTurnType(edge_connection_id, signal_specs)
	return {
		id: `new-${stamp}`,
		key: `${phase_plan_id}-${phase_number}-${stamp}`,
		edge_connection_id: edge_connection_id,
		priority: PRIORITY_ENUM.protected,
		movement_type: resolved_name ? MOVEMENT_TYPE_ENUM.overlap : null,
		overlap_name: resolved_name,
		is_permissive: false,
		is_rtor: turn === TURN_ENUM.right,
	}
}

// ─── Cell renderer types ──────────────────────────────────────────────────────

interface ICellRendererParams {
	signal_id: string
	phase_plan_id: string
	phase_mapping: IPhaseMappingData
	static_data: IPhaseMappingData[] | undefined
	signal_specs: IPhaseMappingSignalData
	phase: IPhaseMapping
	atomic_movement: IAtomicMovement
	active_phases: { id: number; display: number }[]
	onCommit: () => void
}

type FieldKey = 'phase_number' | 'movement' | 'permissive' | 'rtor'
interface IFieldDef {
	key: FieldKey
	heading: string
	render: (params: ICellRendererParams) => m.Children
}

// ─── Cell renderers ───────────────────────────────────────────────────────────

function renderPhaseNumberCell({
	signal_id,
	phase_plan_id,
	phase_mapping,
	static_data,
	phase,
	atomic_movement,
	active_phases,
	onCommit,
}: ICellRendererParams): m.Children {
	const original_phase = static_data
		?.find((plan) => plan.phase_settings_id === phase_plan_id)
		?.phase_mapping?.find((p) => p.atomic_movements?.some((am) => am.key === atomic_movement.key))

	const has_value_changed = original_phase != null && original_phase.number !== phase.number

	const errors = OptimusConfigurationModel.signal_errors[signal_id]['phase-mapping'].errors
	const error_key = `${phase_plan_id}_${phase.number}_${atomic_movement.edge_connection_id}_${atomic_movement.priority}_phase_number`

	const phase_options =
		active_phases.length > 0
			? active_phases.map((p) => ({ id: p.id, value: p.id, label: p.display }))
			: Array.from({ length: 8 }, (_, i) => i + 1).map((n) => ({ id: n, value: n, label: n }))

	return m(SingleSelect as unknown as m.Component<Record<string, unknown>>, {
		id: error_key,
		errors: errors?.[error_key],
		custom_classes: ['phase-select', css.regular_select, has_value_changed && css.input_changed]
			.filter(Boolean)
			.join(' '),
		data: { value: phase.number, label: phase.number ?? '-' },
		onchange: (option: { id: number; value: number; label: number }) => {
			const live_phase = phase_mapping.phase_mapping?.find((p) => p.number === phase.number)
			if (!live_phase) return

			const live_idx = live_phase.atomic_movements.findIndex((am) => am.key === atomic_movement.key)
			const [live_am] = live_idx >= 0 ? live_phase.atomic_movements.splice(live_idx, 1) : [atomic_movement]

			if (live_phase.atomic_movements.length === 0) {
				removeMutate(phase_mapping.phase_mapping, (p) => p.number === live_phase.number)
			}

			const target = phase_mapping.phase_mapping.find((p) => p.number === option.id)
			const already_there = target?.atomic_movements.some(
				(am) =>
					am.edge_connection_id === live_am.edge_connection_id &&
					am.priority === live_am.priority &&
					(am.overlap_name ?? null) === (live_am.overlap_name ?? null),
			)
			if (!already_there) {
				if (target) {
					target.atomic_movements.push(live_am)
				} else {
					phase_mapping.phase_mapping.push({ number: option.id, atomic_movements: [live_am] })
					sortPhases(phase_mapping.phase_mapping)
				}
			}

			markEmptyMappingForDeletion(phase_mapping)
			onCommit()
		},
		options: phase_options,
	})
}

function renderMovementCell({
	signal_id,
	phase_plan_id,
	static_data,
	signal_specs,
	phase,
	atomic_movement,
	onCommit,
}: ICellRendererParams): m.Children {
	const static_movement = findStaticMovement(static_data, phase_plan_id, atomic_movement.key)
	const has_value_changed = static_movement?.edge_connection_id !== atomic_movement.edge_connection_id
	const edge_connections = signal_specs.edge_connections ?? []

	const errors = OptimusConfigurationModel.signal_errors[signal_id]['phase-mapping'].errors
	const error_key = `${phase_plan_id}_${phase.number}_${atomic_movement.edge_connection_id}_${atomic_movement.priority}_movement`

	return m(SingleSelect as unknown as m.Component<Record<string, unknown>>, {
		id: error_key,
		errors: errors?.[error_key],
		custom_classes: ['phase-select', css.regular_select, has_value_changed && css.input_changed]
			.filter(Boolean)
			.join(' '),
		data: {
			value: atomic_movement.edge_connection_id,
			label: edge_connections.find((ec) => ec.id === atomic_movement.edge_connection_id)?.name ?? '-',
		},
		onchange: (option: { id: string; value: string; label: string }) => {
			atomic_movement.edge_connection_id = option.id
			onCommit()
		},
		options: edge_connections.map((ec) => ({ id: ec.id, value: ec.id, label: ec.name })),
	})
}

function renderPermissiveCell({ atomic_movement, onCommit }: ICellRendererParams): m.Children {
	return (
		<InputCheckbox
			checked={!!atomic_movement.is_permissive}
			onchange={(checked: boolean) => {
				atomic_movement.is_permissive = checked
				onCommit()
			}}
		/>
	)
}

function renderRtorCell({ signal_specs, atomic_movement, onCommit }: ICellRendererParams): m.Children {
	const turn = getMovementTurnType(atomic_movement.edge_connection_id, signal_specs)
	const is_right_turn = turn === TURN_ENUM.right
	const checked = is_right_turn ? (atomic_movement.is_rtor ?? true) : false
	return (
		<InputCheckbox
			checked={checked}
			disabled={!is_right_turn}
			class={!is_right_turn ? css.disabled_checkbox : ''}
			onchange={(next: boolean) => {
				if (!is_right_turn) return
				atomic_movement.is_rtor = next
				onCommit()
			}}
		/>
	)
}

// ─── Table structure ──────────────────────────────────────────────────────────

const FIELDS: IFieldDef[] = [
	{ key: 'phase_number', heading: 'Phase #', render: renderPhaseNumberCell },
	{ key: 'movement', heading: 'Movement', render: renderMovementCell },
	{ key: 'permissive', heading: 'Permissive', render: renderPermissiveCell },
	{ key: 'rtor', heading: 'RTOR', render: renderRtorCell },
]

// ─── Component ────────────────────────────────────────────────────────────────

export const PhaseMappingTab = initMithrilComponent<IPhaseMappingTabAttrs, IPhaseMappingTabState>()

PhaseMappingTab.oninit = (vnode) => {
	vnode.state.selected_overlap_view = null
	vnode.state.dropdown_open = false
	vnode.state.new_overlap_name_input = null
	vnode.state.pending_overlap_name = null
	vnode.state.renaming_overlap_name = null
	vnode.state.renaming_overlap_value = ''

	vnode.state.notifyModification = () => {
		if (!vnode.attrs.handleModificationState) return
		const normalized_static = normalizePhaseMappings(vnode.attrs.staticData)
		const normalized_current = normalizePhaseMappings(vnode.state.signal_phase_mappings)
		vnode.attrs.handleModificationState('phase-mapping', !_.isEqual(normalized_static, normalized_current))
	}

	vnode.state.emitMapData = () => {
		const pm = vnode.state.phase_mapping
		vnode.attrs.onMapDataChange({
			signal_specs: vnode.state.signal_specs,
			phase_mapping: pm ? { ...pm, phase_mapping: pm.phase_mapping ? [...pm.phase_mapping] : [] } : undefined,
			active_phases: vnode.state.active_phases,
			selected_overlap_view: vnode.state.selected_overlap_view,
			onPhaseChange: (edge_connection_id, new_phase_number) => {
				if (!vnode.state.phase_mapping) return
				moveEdgeConnectionToPhase(
					vnode.state.phase_mapping,
					edge_connection_id,
					new_phase_number,
					vnode.state.selected_overlap_view,
					vnode.state.signal_specs,
				)
				setTimeout(() => vnode.state.commitMappingUpdate(), 0)
			},
		})
	}

	vnode.state.commitMappingUpdate = () => {
		OptimusConfigurationModel.updateSignalPhaseMappings(vnode.state.signal_id, vnode.state.signal_phase_mappings)
		vnode.state.notifyModification()
		vnode.state.emitMapData()
	}

	vnode.state.renameOverlap = (old_name, new_name) => {
		const trimmed = new_name.trim()
		if (!trimmed || trimmed === old_name) return
		const pm = vnode.state.phase_mapping
		if (!pm) return
		if (collectOverlapNames(pm).includes(trimmed)) return
		for (const p of pm.phase_mapping ?? []) {
			for (const am of p.atomic_movements ?? []) {
				if (am.overlap_name === old_name) am.overlap_name = trimmed
			}
		}
		if (vnode.state.selected_overlap_view === old_name) vnode.state.selected_overlap_view = trimmed
		vnode.state.commitMappingUpdate()
	}

	vnode.state.deleteOverlap = (name) => {
		const pm = vnode.state.phase_mapping
		if (!pm) return
		for (const p of pm.phase_mapping ?? []) {
			p.atomic_movements = (p.atomic_movements ?? []).filter((am) => am.overlap_name !== name)
		}
		if (vnode.state.selected_overlap_view === name) vnode.state.selected_overlap_view = null
		if (vnode.state.renaming_overlap_name === name) vnode.state.renaming_overlap_name = null
		vnode.state.commitMappingUpdate()
	}

	vnode.state.assignNameToRow = (am, raw_name) => {
		const name = raw_name.trim()
		if (!name) return
		am.overlap_name = name
		vnode.state.commitMappingUpdate()
	}

	vnode.state.commitNewOverlap = () => {
		const raw = (vnode.state.new_overlap_name_input ?? '').trim()
		vnode.state.new_overlap_name_input = null
		if (!raw) return
		const overlap_names = collectOverlapNames(vnode.state.phase_mapping)
		if (overlap_names.includes(raw)) {
			vnode.state.selected_overlap_view = raw
			vnode.state.dropdown_open = false
			vnode.state.emitMapData()
			return
		}
		const pm = vnode.state.phase_mapping
		const edge_connections = vnode.state.signal_specs?.edge_connections ?? []
		const first_phase = pm?.phase_mapping?.[0]
		vnode.state.pending_overlap_name = raw
		vnode.state.selected_overlap_view = raw
		vnode.state.dropdown_open = false
		if (pm && first_phase && edge_connections.length > 0) {
			const stamp = Date.now()
			const turn = getMovementTurnType(edge_connections[0].id, vnode.state.signal_specs)
			first_phase.atomic_movements.push({
				id: `new-${stamp}`,
				key: `${pm.phase_settings_id}-${first_phase.number}-${stamp}`,
				edge_connection_id: edge_connections[0].id,
				movement_type: MOVEMENT_TYPE_ENUM.overlap,
				overlap_name: raw,
				priority: PRIORITY_ENUM.protected,
				is_permissive: false,
				is_rtor: turn === TURN_ENUM.right,
			})
			vnode.state.commitMappingUpdate()
		} else {
			vnode.state.emitMapData()
		}
	}

	vnode.state.commitRename = () => {
		const old_name = vnode.state.renaming_overlap_name
		if (!old_name) return
		vnode.state.renameOverlap(old_name, vnode.state.renaming_overlap_value)
		vnode.state.renaming_overlap_name = null
		vnode.state.renaming_overlap_value = ''
	}

	vnode.state.syncFromAttrs = (vnode) => {
		const { loading, headerSelections: sel } = vnode.attrs

		const selected_network_id = sel?.network?.value
		const selected_signal_id = sel?.signal?.value
		const selected_phase_plan_id = sel?.phasePlans?.value
		const selected_phase_plan_external_id = sel?.phasePlans?.label

		const page_key = `${loading}_${selected_network_id}_${selected_signal_id}_${selected_phase_plan_id}`
		const specs_key = `${selected_network_id}_${selected_signal_id}`

		if (page_key === vnode.state.page_key && specs_key === vnode.state.specs_key) return

		if (page_key !== vnode.state.page_key) {
			vnode.state.page_key = page_key
			vnode.state.network_id = selected_network_id ?? ''
			vnode.state.signal_id = selected_signal_id ?? ''
			vnode.state.phase_plan_id = selected_phase_plan_id ?? ''
			vnode.state.phase_plan_external_id = (selected_phase_plan_external_id as string) ?? ''
			vnode.state.should_reload_page = 0

			vnode.state.selected_overlap_view = null
			vnode.state.dropdown_open = false
			vnode.state.new_overlap_name_input = null
			vnode.state.pending_overlap_name = null
			vnode.state.renaming_overlap_name = null

			vnode.state.signal_phase_mappings = normalizePermissive(
				cloneDeep(OptimusConfigurationModel.phase_mappings?.[selected_signal_id ?? ''] ?? []) as IPhaseMappingData[],
			)

			const phase_settings = OptimusConfigurationModel.phase_settings?.[selected_signal_id ?? ''] ?? []
			const selected_setting = phase_settings.find((s) => s.phase_settings_id === selected_phase_plan_id)

			vnode.state.active_phases = Object.entries(selected_setting?.phase_settings_data ?? {})
				.filter(([, data]) => data.enabled)
				.map(([phase]) => ({ id: Number(phase), display: Number(phase) }))

			vnode.state.phase_mapping = vnode.state.signal_phase_mappings.find(
				(pm) => pm.phase_settings_id === selected_phase_plan_id,
			)

			if (vnode.state.phase_mapping?.phase_mapping?.length) {
				sortPhases(vnode.state.phase_mapping.phase_mapping)
			}
		}

		if (specs_key !== vnode.state.specs_key) {
			const signal_specs = OptimusConfigurationModel.signals?.find((s) => s.id === selected_signal_id) as
				| IPhaseMappingSignalData
				| undefined

			if (signal_specs) {
				signal_specs.edge_connections = getEdgeConnectionNames({
					abbreviate_turns: true,
					edge_connections: signal_specs.edge_connections,
					edge_connection_id: null,
				}).sort(sortByDirectionAndTurn)

				vnode.state.signal_specs = signal_specs
				vnode.state.specs_key = specs_key
			}
		}

		vnode.state.emitMapData()
		m.redraw()
	}

	vnode.state.syncFromAttrs(vnode)
}

PhaseMappingTab.onbeforeupdate = (vnode) => {
	vnode.state.syncFromAttrs(vnode)

	const reload_changed = vnode.attrs.should_reload_page !== vnode.state.should_reload_page
	const signal_val = vnode.attrs.headerSelections?.signal?.value
	const phase_plan_id = vnode.attrs.headerSelections?.phasePlans?.value

	if (!reload_changed || !signal_val || !phase_plan_id) return

	const fresh_mappings = normalizePermissive(
		cloneDeep(OptimusConfigurationModel.phase_mappings[signal_val] ?? []) as IPhaseMappingData[],
	)

	vnode.state.should_reload_page = vnode.attrs.should_reload_page
	vnode.state.signal_phase_mappings = fresh_mappings
	vnode.state.phase_mapping = fresh_mappings.find((pm) => pm.phase_settings_id === phase_plan_id)

	vnode.state.notifyModification()
	vnode.state.emitMapData()
	m.redraw()
}

PhaseMappingTab.view = (vnode) => {
	const { signal_id, phase_plan_id, phase_mapping, active_phases, signal_specs } = vnode.state

	if (signal_id == null || phase_plan_id == null) {
		return (
			<div className={css.contents}>
				<div className={css.no_signal_selected_message}>Signal and Phase Plan Required</div>
			</div>
		)
	}

	const errors = OptimusConfigurationModel.signal_errors[signal_id]['phase-mapping']
	const config_errors = OptimusConfigurationModel.signal_errors?.[signal_id]?.['completed-configurations']
	const warnings = vnode.attrs.warnings['phase-mapping']

	const unique_error_pairs = Object.entries(errors.errors as Record<string, string>).reduce<[string, string][]>(
		(acc, [key, msg]) => {
			if (!acc.some(([, m]) => m === msg)) acc.push([key, msg])
			return acc
		},
		[],
	)

	const edge_connections = signal_specs?.edge_connections ?? []
	const view = vnode.state.selected_overlap_view
	const is_admin_unnamed_view = view === ADMIN_UNNAMED_VIEW

	return (
		<div className={css.contents}>
			<div className={css.body}>
				{config_errors?.count > 0 && <ValidationErrorMessaging errors={config_errors.errors ?? {}} />}
				{errors.count > 0 && (
					<div className={css.error_container}>
						<ValidationErrorMessaging errors={Object.fromEntries(unique_error_pairs)} warnings={warnings} />
					</div>
				)}
				<OverlapViewSwitcher
					phase_mapping={phase_mapping}
					selected_overlap_view={vnode.state.selected_overlap_view}
					dropdown_open={vnode.state.dropdown_open}
					new_overlap_name_input={vnode.state.new_overlap_name_input}
					renaming_overlap_name={vnode.state.renaming_overlap_name}
					renaming_overlap_value={vnode.state.renaming_overlap_value}
					onSelectView={(v) => {
						vnode.state.selected_overlap_view = v
						vnode.state.dropdown_open = false
						vnode.state.emitMapData()
					}}
					onToggleDropdown={() => {
						vnode.state.dropdown_open = !vnode.state.dropdown_open
					}}
					onCloseDropdown={() => {
						vnode.state.dropdown_open = false
					}}
					onStartRename={(name) => {
						vnode.state.renaming_overlap_name = name
						vnode.state.renaming_overlap_value = name
					}}
					onRenameValueChange={(value) => {
						vnode.state.renaming_overlap_value = value
					}}
					onCommitRename={vnode.state.commitRename}
					onCancelRename={() => {
						vnode.state.renaming_overlap_name = null
						vnode.state.renaming_overlap_value = ''
					}}
					onDeleteOverlap={vnode.state.deleteOverlap}
					onNewOverlapInputChange={(value) => {
						vnode.state.new_overlap_name_input = value
					}}
					onCommitNewOverlap={vnode.state.commitNewOverlap}
					onCancelNewOverlap={() => {
						vnode.state.new_overlap_name_input = null
					}}
					onStartNewOverlap={() => {
						vnode.state.new_overlap_name_input = ''
					}}
				/>
				<div className={css.scrollable}>
					<table className={[css.table, is_admin_unnamed_view ? css.table_admin : ''].filter(Boolean).join(' ')}>
						<thead>
							<tr>
								{FIELDS.map((field) => (
									<th
										key={`fields-${field.key}`}
										className={[
											css.heading_text,
											field.key === 'permissive' || field.key === 'rtor' ? css.overlap_heading : '',
										]
											.filter(Boolean)
											.join(' ')}
									>
										{field.heading}
									</th>
								))}
								<th />
							</tr>
						</thead>
						<tbody>
							{phase_mapping?.phase_mapping?.length ? (
								phase_mapping.phase_mapping.flatMap((phase) => {
									const visible_movements = (phase.atomic_movements ?? []).filter((am) => filterForView(am, view))

									if (!visible_movements.length) {
										if (view !== null) return []
										return [
											<tr
												key={`phase-mapping-phase-${phase.number}`}
												style={{ 'grid-column-start': `span ${FIELDS.length}` }}
												className={css.empty_message}
											>
												<td>{`No mappings for Phase ${phase.number}.`}</td>
											</tr>,
											<tr key={`render-add-movement-row-add-${phase.number}`}>
												<td colspan={FIELDS.length + 1}>
													<UnstyledButton
														type="button"
														className={css.add_movement_button}
														onclick={() => {
															if (!edge_connections.length) return
															phase.atomic_movements.push(
																buildNewMovement(
																	phase_plan_id,
																	phase.number,
																	edge_connections[0].id,
																	view,
																	signal_specs,
																),
															)
															vnode.state.commitMappingUpdate()
														}}
													>
														{`+ Add Movement to Phase ${phase.number}`}
													</UnstyledButton>
												</td>
											</tr>,
										]
									}

									return visible_movements.map((atomic_movement) => {
										const am_idx = phase.atomic_movements.indexOf(atomic_movement)
										const error_keys = Object.keys(errors.errors)
										const is_row_error = error_keys.some((key) =>
											key.includes(`${phase_plan_id}_${phase.number}_${atomic_movement.edge_connection_id}`),
										)
										const cell_params: ICellRendererParams = {
											signal_id,
											phase_plan_id,
											phase_mapping,
											static_data: vnode.attrs.staticData,
											signal_specs,
											phase,
											atomic_movement,
											active_phases,
											onCommit: vnode.state.commitMappingUpdate,
										}

										return (
											<tr
												key={`${phase.number}-${atomic_movement.edge_connection_id}-${atomic_movement.priority}`}
												className={is_row_error ? css.row_error : ''}
											>
												{FIELDS.map((field) => (
													<td key={`atomic-movement-field-${field.key}`}>{field.render(cell_params)}</td>
												))}
												<td className={css.end_buttons_cell}>
													{is_admin_unnamed_view ? (
														<input
															type="text"
															className={css.admin_row_name_input}
															placeholder="Name…"
															title="Assign an overlap name to this row"
															onkeydown={(e: KeyboardEvent) => {
																if (e.key !== 'Enter') return
																e.preventDefault()
																const target = e.target as HTMLInputElement
																vnode.state.assignNameToRow(atomic_movement, target.value)
																target.value = ''
															}}
															onblur={(e: FocusEvent) => {
																const target = e.target as HTMLInputElement
																if (!target.value.trim()) return
																vnode.state.assignNameToRow(atomic_movement, target.value)
																target.value = ''
															}}
														/>
													) : (
														<IconButton
															icon={PlusCircleIcon}
															class={css.icon_button}
															title="Add movement"
															onClick={() => {
																if (!edge_connections.length) return
																phase.atomic_movements.push(
																	buildNewMovement(
																		phase_plan_id,
																		phase.number,
																		edge_connections[0].id,
																		view,
																		signal_specs,
																	),
																)
																vnode.state.commitMappingUpdate()
															}}
														/>
													)}
													<IconButton
														icon={CloseIcon}
														class={[css.icon_button, css.delete_icon_button].join(' ')}
														title="Delete movement"
														onClick={() => {
															phase.atomic_movements.splice(am_idx, 1)
															if (!phase.atomic_movements.length) {
																removeMutate(phase_mapping.phase_mapping, (p) => p.number === phase.number)
																markEmptyMappingForDeletion(phase_mapping)
															}
															OptimusConfigurationModel.updateSignalPhaseMappings(
																signal_id,
																vnode.state.signal_phase_mappings,
															)
															vnode.state.commitMappingUpdate()
														}}
													/>
												</td>
											</tr>
										)
									})
								})
							) : (
								<tr style={{ 'grid-column-start': `span ${FIELDS.length}` }} className={css.empty_message}>
									<td>No mappings for Signal yet.</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}
